import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ChevronDown, Calendar, FileText, Briefcase, Globe2, Trophy, AlertTriangle,
} from 'lucide-react';
import { usePageMeta, useFAQSchema } from '@/hooks/useSEO';

/**
 * StudentVisaGuideShell
 * ─────────────────────
 * Config-driven shell that powers all Student-Visa Guide pages built on the
 * Germany blueprint (Australia, Canada — and future UK / Ireland).
 *
 * Each page is now a thin config import:  <StudentVisaGuideShell config={CONFIG} />
 * The shell handles SEO meta, FAQ + Breadcrumb JSON-LD injection, and every
 * section render. Country-specific data lives entirely in the config object.
 */

/* ─── Subcomponents (internal) ────────────────────────────────────────── */

/**
 * RichText
 * ────────
 * Renders trusted, config-driven strings that contain inline `<strong>` /
 * `<b>` tags as safe React elements — no `dangerouslySetInnerHTML`.
 * Avoids the XSS surface area entirely, even though the content is
 * developer-authored, so a future contributor cannot accidentally pipe
 * untrusted data into the shell.
 */
const RichText = ({ text }) => {
  if (!text) return null;
  // Split on <strong>...</strong> or <b>...</b> while keeping the captures.
  const parts = text.split(/(<\/?(?:strong|b)(?:\s[^>]*)?>)/i);
  let bold = false;
  return parts.map((part, i) => {
    if (/^<(strong|b)(\s[^>]*)?>$/i.test(part)) { bold = true; return null; }
    if (/^<\/(strong|b)>$/i.test(part))          { bold = false; return null; }
    if (!part) return null;
    return bold
      ? <strong key={`rt-${i}`} className="text-white">{part}</strong>
      : <span key={`rt-${i}`}>{part}</span>;
  });
};

const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-3xl mb-8 lg:mb-12">
    {eyebrow && (
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/12 border border-[#C5A028]/35 text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
        {eyebrow}
      </div>
    )}
    <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne text-[#003087] leading-tight mb-3">{title}</h2>
    {subtitle && <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{subtitle}</p>}
  </div>
);

const BulletDot = ({ tone = 'gold' }) => {
  const m = { gold: 'bg-[#C5A028]', blue: 'bg-[#003087]', emerald: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500' };
  return <span className={`mt-1.5 inline-flex shrink-0 w-2 h-2 rounded-full ${m[tone] || m.gold}`} />;
};

const FaqItem = ({ q, a, index, prefix }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-2xl transition-all ${open ? 'border-[#C5A028]/50 bg-white shadow-md' : 'border-slate-200 bg-white'}`} data-testid={`${prefix}-faq-item-${index}`}>
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5" data-testid={`${prefix}-faq-toggle-${index}`}>
        <span className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">{q}</span>
        <ChevronDown className={`shrink-0 w-5 h-5 mt-0.5 text-[#C5A028] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-[15px] text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
};

const CtaInline = ({ tag, heading, copy, button, href, testid }) => (
  <div className="my-10 lg:my-14 rounded-3xl p-6 sm:p-9 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }} data-testid={testid}>
    <div aria-hidden="true" className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#C5A028]/14 blur-3xl" />
    <div className="relative grid lg:grid-cols-[1fr_auto] gap-6 items-center">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/35 text-[10.5px] font-bold tracking-[0.2em] text-[#FACC15] uppercase mb-3">{tag}</div>
        <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold font-syne leading-tight mb-2">{heading}</h3>
        <p className="text-sm sm:text-[15px] text-slate-300 leading-relaxed max-w-xl">{copy}</p>
      </div>
      <Link to={href} className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5 whitespace-nowrap">
        {button}<ArrowUpRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);

/* ─── Main Shell ──────────────────────────────────────────────────────── */

const StudentVisaGuideShell = ({ config: c }) => {
  /* SEO meta — uses the canonical usePageMeta() chain (title + meta description
   * + canonical + OG/Twitter tags). Replaces the custom document.title patch
   * that was previously bypassing the SEO authority — Feb 2026 orphan-pages
   * cleanup. Now Google + crawlers + dashboard all see the right title for
   * each student-visa page instead of inheriting the homepage default. */
  usePageMeta(c.seo.title, c.seo.description);

  /* FAQ schema — uses the global useFAQSchema() hook (single-authority,
   * dedupe-safe). Previously inline-injected with a custom `data-${prefix}-faq-schema`
   * marker that confused Google's Rich Results validator (one of 22 inline
   * injectors flagged by the SEO Health Dashboard). Migrating to the hook
   * automatically reduces the dashboard's `inline_faq_injectors_count`. */
  useFAQSchema(c.faqs);

  /* Breadcrumb JSON-LD only — kept as a local useEffect because it's
   * page-specific and there's no shared hook for breadcrumbs yet. */
  useEffect(() => {
    const bcSchema = {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: c.breadcrumb.map((b, i) => ({
        '@type': 'ListItem', position: i + 1, name: b.name, ...(b.url ? { item: b.url } : {}),
      })),
    };
    const bcEl = document.createElement('script');
    bcEl.type = 'application/ld+json';
    bcEl.setAttribute(`data-${c.testidPrefix}-bc-schema`, '1');
    bcEl.text = JSON.stringify(bcSchema);
    document.head.appendChild(bcEl);
    return () => {
      document.head.querySelector(`script[data-${c.testidPrefix}-bc-schema]`)?.remove();
    };
  }, [c.breadcrumb, c.testidPrefix]);

  const P = c.testidPrefix;

  return (
    <div className="bg-white zaf-prose" data-testid={`${P}-page`}>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}>
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-24">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
                <Globe2 className="w-3.5 h-3.5" /> {c.hero.eyebrow}
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-[10.5px] font-bold tracking-[0.22em] text-emerald-200 uppercase" data-testid={`${P}-updated-pill`}>
                <Calendar className="w-3.5 h-3.5" /> Last Updated: {c.hero.lastUpdated}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-[52px] font-bold font-syne leading-[1.05] tracking-tight mb-5" data-testid={`${P}-h1`}>
              {c.hero.titlePre} <span className="text-[#FACC15]">{c.hero.titleAccent}</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl mb-6">
              <RichText text={c.hero.summaryHtml} />
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={c.hero.primaryHref} className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5" data-testid={`${P}-hero-cta`}>
                {c.hero.primaryCta} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={c.hero.secondaryHref} className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all">
                {c.hero.secondaryCta}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINANCIAL SNAPSHOT ============ */}
      <section className="py-12 lg:py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="zaf-financial-snapshot rounded-3xl p-7 sm:p-12 grid lg:grid-cols-[auto_1fr] gap-8 items-center" data-testid={`${P}-financial-snapshot`}>
            <div className="text-center lg:text-left">
              <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-amber-800 mb-2">{c.snapshot.eyebrow}</div>
              <div className="zaf-financial-snapshot-amount text-5xl sm:text-6xl lg:text-7xl mb-1">{c.snapshot.amount}</div>
              <div className="text-sm font-semibold text-amber-900">{c.snapshot.subLabel}</div>
            </div>
            <div className="space-y-2.5 text-sm sm:text-[15px] text-amber-900/90">
              {c.snapshot.paragraphsHtml.map((html) => (
                <p key={html.slice(0, 40)} className="leading-relaxed">
                  <RichText text={html} />
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ TIMELINE ============ */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading {...c.timeline.heading} />
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto" data-testid={`${P}-timeline-table`}>
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-100/70 text-[10.5px] tracking-[0.18em] uppercase text-slate-600">
                  <th className="px-5 sm:px-7 py-4 text-left font-bold">Timeline</th>
                  <th className="px-5 sm:px-7 py-4 text-left font-bold">Action Item</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {c.timeline.rows.map(t => (
                    <tr key={t.window} className="hover:bg-slate-50">
                      <td className="px-5 sm:px-7 py-4 font-bold text-slate-900 whitespace-nowrap">{t.window}</td>
                      <td className="px-5 sm:px-7 py-4 text-slate-700">{t.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINANCIAL PROOF TABLE (optional) ============ */}
      {c.financialProof && (
        <section className="py-14 lg:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeading {...c.financialProof.heading} />
            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto" data-testid={`${P}-financial-table`}>
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-100/70 text-[10.5px] tracking-[0.18em] uppercase text-slate-600">
                    <th className="px-5 sm:px-7 py-4 text-left font-bold">Cost Component</th>
                    <th className="px-5 sm:px-7 py-4 text-left font-bold">Amount</th>
                    <th className="px-5 sm:px-7 py-4 text-left font-bold hidden md:table-cell">Notes</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {c.financialProof.rows.map(r => (
                      <tr key={r.item} className="hover:bg-slate-50">
                        <td className="px-5 sm:px-7 py-4 font-semibold text-slate-900">{r.item}</td>
                        <td className="px-5 sm:px-7 py-4 font-bold text-[#003087]">{r.amount}</td>
                        <td className="px-5 sm:px-7 py-4 text-slate-700 hidden md:table-cell">{r.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {c.financialProof.cta && <CtaInline {...c.financialProof.cta} />}
          </div>
        </section>
      )}

      {/* ============ POLICY ALERTS ============ */}
      {c.policyAlerts && (
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeading {...c.policyAlerts.heading} />
            <div className={`grid gap-5 lg:gap-7 ${c.policyAlerts.items.length >= 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
              {c.policyAlerts.items.map((u) => (
                <div key={u.title} className="zaf-alert-policy rounded-2xl p-6 sm:p-7" data-testid={`${P}-alert-${u.title.split(' ').slice(0, 2).join('-').toLowerCase()}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />
                    <h3 className="text-base sm:text-lg font-bold font-syne text-amber-900">{u.title}</h3>
                  </div>
                  {u.date && <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-amber-800 mb-2">Effective {u.date}</p>}
                  <p className="text-sm text-amber-900/90 leading-relaxed">{u.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ DOCUMENT CHECKLIST ============ */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading {...c.checklists.heading} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-testid={`${P}-checklist-grid`}>
            {c.checklists.cards.map(({ tag, Icon, items, tone }) => (
              <div key={tag} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#C5A028]/40 transition-all" data-testid={`${P}-checklist-${tag.toLowerCase()}`}>
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-[#003087]/8 flex items-center justify-center"><Icon className="w-4 h-4 text-[#003087]" /></div>
                  <h3 className="text-sm font-bold tracking-[0.18em] uppercase text-[#003087]">{tag}</h3>
                </div>
                <ul className="space-y-2">
                  {items.map(it => (
                    <li key={it} className="flex items-start gap-2"><BulletDot tone={tone} /><span className="text-xs sm:text-[13px] text-slate-700 leading-relaxed">{it}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROVIDER COMPARISON (optional) ============ */}
      {c.providers && (
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeading {...c.providers.heading} />
            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto" data-testid={`${P}-providers-table`}>
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-100/70 text-[10.5px] tracking-[0.18em] uppercase text-slate-600">
                    {c.providers.columns.map((col) => (
                      <th key={col.key} className={`px-4 sm:px-6 py-4 text-left font-bold ${col.hide || ''}`}>{col.label}</th>
                    ))}
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {c.providers.rows.map(p => (
                      <tr key={p.name} className="hover:bg-slate-50">
                        {c.providers.columns.map((col) => (
                          <td key={col.key} className={`px-4 sm:px-6 py-5 ${col.bold ? 'font-bold' : ''} ${col.accent ? 'text-[#003087]' : 'text-slate-700'} ${col.hide || ''}`}>
                            {p[col.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ APPLICATION STEPS (optional) ============ */}
      {c.steps && (
        <section className="py-14 lg:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeading {...c.steps.heading} />
            <ol className="space-y-3" data-testid={`${P}-steps`}>
              {c.steps.items.map((s, i) => (
                <li key={s.title} className="bg-white border border-slate-200 rounded-2xl p-5 flex gap-4">
                  <span className="w-9 h-9 rounded-full bg-[#003087] text-white flex items-center justify-center font-bold flex-shrink-0">{i + 1}</span>
                  <div>
                    <h3 className="font-bold font-syne text-[#003087] mb-1">{s.title}</h3>
                    <p className="text-sm text-slate-700 leading-relaxed">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* ============ INTERVIEW + POST-ARRIVAL (optional 2-col) ============ */}
      {(c.interview || c.postArrival) && (
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-7 lg:gap-10">
              {c.interview && (
                <div>
                  <SectionHeading {...c.interview.heading} />
                  <ul className="space-y-3" data-testid={`${P}-interview-qs`}>
                    {c.interview.questions.map((q, i) => (
                      <li key={q} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex gap-3"><FileText className="w-5 h-5 text-[#003087] flex-shrink-0 mt-0.5" /><span className="text-sm text-slate-800 leading-relaxed"><strong className="text-[#003087]">Q{i + 1}.</strong> {q}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {c.postArrival && (
                <div>
                  <SectionHeading {...c.postArrival.heading} />
                  <ul className="space-y-3" data-testid={`${P}-post-arrival`}>
                    {c.postArrival.items.map((item) => (
                      <li key={item} className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3"><Trophy className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" /><span className="text-sm text-emerald-900 leading-relaxed">{item}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============ FAQ ============ */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading {...c.faqsHeading} />
          <div className="space-y-3" data-testid={`${P}-faq-list`}>
            {c.faqs.map((f, i) => <FaqItem key={f.q} q={f.q} a={f.a} index={i} prefix={P} />)}
          </div>
        </div>
      </section>

      {/* ============ RELATED LINKS ============ */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading {...c.anchorLinks.heading} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`${P}-anchor-links`}>
            {c.anchorLinks.items.map((a) => (
              <Link key={a.path} to={a.path} className="group bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-[#C5A028] hover:shadow-sm transition-all flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-[#003087]">{a.label}</span>
                <ArrowUpRight className="w-4 h-4 text-[#C5A028] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-14 lg:py-20" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
        <div className="max-w-5xl mx-auto px-6 text-center text-white">
          <Briefcase className="w-10 h-10 text-[#FACC15] mx-auto mb-4" />
          <h2 className="text-2xl md:text-4xl font-bold font-syne mb-4">{c.finalCta.title}</h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-7 leading-relaxed">{c.finalCta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={`https://wa.me/919990533327?text=${encodeURIComponent(c.finalCta.whatsappText)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-7 py-3.5 rounded-full transition-colors" data-testid={`${P}-final-whatsapp-cta`}>
              WhatsApp +91-9990533327
            </a>
            <Link to={`/study-abroad/book-counselling/?source=${c.finalCta.counsellingSource}`} className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 font-semibold px-7 py-3.5 rounded-full transition-colors" data-testid={`${P}-final-counselling-cta`}>
              Book Free Counselling
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudentVisaGuideShell;
